import { createSlice } from '@reduxjs/toolkit';
import { addPxSuffix } from '../utils/addPxSuffix';

const styleConfigSlice = createSlice({
  name: 'styleConfig',
  initialState: {
    config: {
    // Appearance
      themeColor: '#2020FF', // 主题色 - 纯色
      textColor: '0', // 文本颜色 - 0: Dark | 1: Light
      backgroundColor: '#F9F9FB', // 整体背景颜色 - 图片 | 纯色 | 渐变
      topBarBackgroundColor: '#FCFCFC', // 顶部栏背景颜色 - 纯色 | 渐变
      sideBarBackgroundColor: 'linear-gradient(180deg, #F4F5FA 0%, #F5F4FA 100%)', // 侧边栏背景颜色 - 纯色 | 渐变
      popUpColor: '#FCFCFC', // 弹窗背景颜色 - 纯色 | 渐变
      hoverColor: '#DEE2FF', // 悬停颜色 - 纯色
      // Button
      mainButtonPopUpColor: 'linear-gradient(110deg, #A5A5F7 -20.91%, #7070F7 57.11%, #A5A5F7 130.76%)', // 主按钮背景色 - 纯色 | 渐变
      // Message
      msgAreaColor: 'linear-gradient(141deg, #F4F5FA 6.26%, #F6F4FA 77.43%)', // 消息区域背景色 - 纯色 | 渐变
      welcomeMsgBackground: 'linear-gradient(104deg, rgba(156, 151, 250, 0.15) 0.94%, rgba(167, 114, 207, 0.15) 46.5%, rgba(110, 143, 249, 0.15) 86.7%)', // 欢迎语背景 - 图片 | 纯色 | 渐变
      inputBoxPopUpColor: '#FCFCFC', // 输入框背景色 - 纯色 | 渐变
      userMsgBubbleColor: '#D2C6FE', // 用户提问气泡背景色 - 纯色 | 渐变
      botBubbleColor: 'linear-gradient(107deg, #FDFDFD -5.14%, #FFF 52.41%, #F7F9FD 95.84%)', // 智能体回答气泡背景色 - 纯色 | 渐变
      // Appearance 高级配置
      roundedCorners: '16px', // 圆角半径
      roundedCornersMedium: '12px',
      roundedCornersSmall: '6px',
      // Button 高级配置
      mainButtonTextColor: '#F9F8FF', // 主按钮文本颜色 - 纯色
      mainButtonBorderTrace: 'linear-gradient(329deg, #FFFFFF 0%, #999999 50%, #FFFFFF 100%)', // 主按钮边框色 - 纯色 | 渐变
      auxiliaryButtonTextColor: '#2020FF', // 辅助按钮文本颜色 - 纯色
      auxiliaryButtonBorderTrace: '#2020FF', // 辅助按钮边框颜色 - 纯色 | 渐变
      // Message 高级配置
      msgAreaBorderColor: 'transparent', // 消息区域边框色 - 纯色 | 渐变
      welcomeTextColor: '#1E293B', // 欢迎语文本颜色 - 纯色
      inputBoxBorderColor: 'linear-gradient(276deg, #AAAAFF 6%, #8686F4 25%, #E4C1F2 47%, #AAAAFF 74%)', // 输入框边框颜色 - 纯色 | 渐变
      inputBoxInnerShadow: '#D4D4F8', // 输入框内阴影颜色 - 纯色
      userMsgTextColor: '#1E293B', // 用户提问文案颜色 - 纯色
      userMsgBubbleBorderColor: 'transparent', // 用户提问气泡边框颜色 - 纯色 | 渐变
      botTextColor: '#1E293B', // 智能体回答文案颜色 - 纯色
      botBubbleBorderColor: 'transparent', // 智能体回答气泡边框颜色 - 纯色 | 渐变
      botBubbleInnerShadow: 'transparent' // 智能体回答气泡内阴影颜色 - 纯色
    }
  },
  reducers: {
    setStyleConfig (state, { payload }) {
      const data = {
        ...payload,
        roundedCorners: addPxSuffix(payload?.roundedCorners),
        roundedCornersMedium: addPxSuffix(payload?.roundedCornersMedium),
        roundedCornersSmall: addPxSuffix(payload?.roundedCornersSmall),
        inputBoxInnerShadow: `0 0 20px 0 ${payload?.inputBoxInnerShadow} inset`,
        botBubbleInnerShadow: `0 2px 4px 0 ${payload?.botBubbleInnerShadow} inset`,
        mainTextColor: payload?.textColor === '0' ? '#1E293B' : '#E6E6E6', // 正文文字颜色
        tipTextColor: '#8E949D', // 提示文字颜色
        auxiliaryTextColor: '#626A76', // 辅助文字颜色
        disabledBgColor: payload?.textColor === '0' ? '#AFAFBE' : '#5E5867', // 禁止点击 - 按钮或者图标颜色
        disabledTextColor: payload?.textColor === '0' ? '#FFFFFF' : '#817D84' // 禁止点击 - 按钮或者图标内部文字图标颜色
      };
      // 种植样式公共变量
      document.documentElement.style.setProperty('--themeColor', data?.themeColor);
      document.documentElement.style.setProperty('--popUpColor', data?.popUpColor);
      document.documentElement.style.setProperty('--hoverColor', data?.hoverColor);
      document.documentElement.style.setProperty('--tipTextColor', data?.tipTextColor);
      document.documentElement.style.setProperty('--mainTextColor', data?.mainTextColor);
      document.documentElement.style.setProperty('--auxiliaryTextColor', data?.auxiliaryTextColor);
      // btn
      document.documentElement.style.setProperty('--mainButtonTextColor', data?.mainButtonTextColor);
      document.documentElement.style.setProperty('--mainButtonPopUpColor', data?.mainButtonPopUpColor);
      document.documentElement.style.setProperty('--mainButtonBorderTrace', data?.mainButtonBorderTrace);
      document.documentElement.style.setProperty('--auxiliaryButtonTextColor', data?.auxiliaryButtonTextColor);
      document.documentElement.style.setProperty('--auxiliaryButtonBorderTrace', data?.auxiliaryButtonBorderTrace);
      document.documentElement.style.setProperty('--disabledBgColor', data?.disabledBgColor);
      document.documentElement.style.setProperty('--disabledTextColor', data?.disabledTextColor);
      // roundedCorners
      document.documentElement.style.setProperty('--roundedCorners', data?.roundedCorners);
      document.documentElement.style.setProperty('--roundedCornersMedium', data?.roundedCornersMedium);
      document.documentElement.style.setProperty('--roundedCornersSmall', data?.roundedCornersSmall);
      // message
      document.documentElement.style.setProperty('--msgAreaColor', data?.msgAreaColor);
      document.documentElement.style.setProperty('--msgAreaBorderColor', data?.msgAreaBorderColor);
      document.documentElement.style.setProperty('--inputBoxPopUpColor', data?.inputBoxPopUpColor);
      document.documentElement.style.setProperty('--inputBoxInnerShadow', data?.inputBoxInnerShadow);
      document.documentElement.style.setProperty('--inputBoxBorderColor', data?.inputBoxBorderColor);
      // 用户提问气泡
      document.documentElement.style.setProperty('--userMsgBubbleColor', data?.userMsgBubbleColor);
      document.documentElement.style.setProperty('--userMsgTextColor', data?.userMsgTextColor);
      document.documentElement.style.setProperty('--userMsgBubbleBorderColor', data?.userMsgBubbleBorderColor);
      // 智能体回答气泡
      document.documentElement.style.setProperty('--botBubbleColor', data?.botBubbleColor);
      document.documentElement.style.setProperty('--botTextColor', data?.botTextColor);
      document.documentElement.style.setProperty('--botBubbleInnerShadow', data?.botBubbleInnerShadow);
      document.documentElement.style.setProperty('--botBubbleBorderColor', data?.botBubbleBorderColor);
      state.config = data;
    }
  }
});

export const { setStyleConfig } = styleConfigSlice.actions;
export default styleConfigSlice.reducer;
